import type { KeyboardEvent, ReactNode } from "react";
import { styled } from "@pigment-css/react";
import { Calendar, Checkmark, Note, Repeat, TodosIcon, UserOutline } from "@acto/web-common/components";
import { pressInteraction } from "@acto/taskpane/field-sales-2/styles/pressInteraction";

// Bleeds past the section's content column so the hover reads as a row highlight, not a box cut
// flush at the checkbox.
const Row = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.fieldSalesTwo.spacing[12],
  alignItems: "flex-start",
  paddingBlock: theme.fieldSalesTwo.spacing[16],
  paddingInline: theme.fieldSalesTwo.spacing[12],
  marginInline: `-${theme.fieldSalesTwo.spacing[12]}`,
  borderRadius: theme.fieldSalesTwo.radius[16],
  transition: `background-color ${theme.fieldSalesTwo.motion.duration.fast} ${theme.fieldSalesTwo.motion.easing.standard}`,
  '&[data-state="completed"]': {
    paddingBlock: theme.fieldSalesTwo.spacing[12],
    alignItems: "center",
  },
  '&[data-clickable="true"]': { cursor: "pointer" },
  '&[data-clickable="true"]:hover': { background: "rgba(19, 19, 19, 0.04)" },
  '&[data-clickable="true"]:active': { background: "rgba(19, 19, 19, 0.08)" },
}));

// `pressInteraction` is spread first so the `:active` override below keeps its focus ring +
// reduced-motion guard while adding the surface tint.
const Checkbox = styled("button")(({ theme }) => ({
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: theme.fieldSalesTwo.spacing[20],
  height: theme.fieldSalesTwo.spacing[20],
  // Small top offset so the 20px box optically centers with the title's first line (which sits
  // high in its line box). The box is shorter than the row, so this never affects the row height.
  marginTop: 1,
  padding: 0,
  // Squircle, not a full circle: radius[8] on the 20px box (radius[10] = 10px would be 50% = circular).
  borderRadius: theme.fieldSalesTwo.radius[8],
  border: `1.5px solid ${theme.fieldSalesTwo.colors.neutral[500]}`,
  background: "transparent",
  color: theme.fieldSalesTwo.colors.status.success.main,
  cursor: "pointer",
  WebkitTapHighlightColor: "transparent",
  transition: "background 120ms ease, border-color 120ms ease, transform 120ms ease",
  "& svg": { width: theme.fieldSalesTwo.spacing[12], height: theme.fieldSalesTwo.spacing[12] },
  ...pressInteraction(theme),
  "&:hover": {
    borderColor: theme.fieldSalesTwo.colors.neutral[700],
    background: theme.fieldSalesTwo.colors.surface.glassMedium,
  },
  "&:active:not(:disabled)": {
    transform: "scale(0.96)",
    background: theme.fieldSalesTwo.colors.surface.glassStrong,
  },
  "&:disabled": {
    cursor: "default",
    opacity: 0.5,
  },
  // Optimistic checked state (a completion is pending/committing): a green check in the unchanged
  // box (transparent bg, neutral border), at full opacity over the dimmed `:disabled` look.
  '&[data-checked="true"]': {
    opacity: 1,
  },
  '&[data-checked="completed"]': {
    border: "none",
    background: theme.fieldSalesTwo.colors.neutral[200],
    color: theme.fieldSalesTwo.colors.neutral[700],
    opacity: 1,
  },
  '&[data-checked="completed"]:hover': {
    background: theme.fieldSalesTwo.colors.neutral[300],
  },
}));

const Body = styled("div")({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minWidth: 0,
});

// Inline, brand-coloured "@Customer" prefix that flows with the title text (wraps onto multiple
// lines with it — never truncated). Rendered as a link so a tap opens the customer without
// triggering the row's own completion; `stopPropagation` keeps the two interactions separate.
const CustomerLink = styled("span")(({ theme }) => ({
  color: theme.fieldSalesTwo.colors.charts.primary,
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  cursor: "pointer",
  "&:focus-visible": {
    outline: `${theme.strokeWidth.regular} solid ${theme.fieldSalesTwo.colors.charts.primary}`,
    outlineOffset: "2px",
    borderRadius: theme.fieldSalesTwo.radius[10],
  },
  '&[data-state="completed"]': {
    color: theme.fieldSalesTwo.colors.neutral[800],
  },
}));

const Title = styled("p")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size.base,
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[900],
  '&[data-state="completed"]': {
    color: theme.fieldSalesTwo.colors.neutral[800],
    textDecoration: "line-through",
  },
}));

const Meta = styled("div")(({ theme }) => ({
  marginTop: theme.fieldSalesTwo.spacing[10],
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[6],
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[14],
  fontWeight: theme.fieldSalesTwo.font.weight[440],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[800],
  "& svg": {
    width: 14,
    height: 14,
    flexShrink: 0,
  },
}));

const MetaSeparator = styled("span")(({ theme }) => ({
  color: theme.fieldSalesTwo.colors.neutral[800],
  padding: `0 ${theme.fieldSalesTwo.spacing[2]}`,
}));

const MetaPiece = styled("span")(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[6],
}));

const Overdue = styled("span")(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[4],
}));

type TodoKind = "workflow" | "recap" | "todo";

interface TodoListItemProps {
  title: string;
  isCompleted?: boolean;
  kind?: TodoKind;
  kindLabel?: string;
  dueDateLabel?: string;
  overdueLabel?: string;
  assigneeName?: string;
  /** Cross-customer lists (the global Todos page) show a leading "@Customer" link. Omit for the
   * client-scoped list, where the customer is implied. */
  customerName?: string;
  /** Opens the linked customer. Only meaningful alongside `customerName`. */
  onCustomerClick?: () => void;
  /** Marks the todo done. Only wired for active items; absent ⇒ the checkbox is inert. */
  onComplete?: () => void;
  /** Reverts a completed todo back to open. Only wired for completed items; absent ⇒ the checkbox
   * shows as checked but is inert. */
  onUncomplete?: () => void;
  /** Opens the row's edit surface. Tapping the checkbox or the @customer link is excluded (both
   * stop propagation). Absent ⇒ the row is not interactive as a whole (e.g. completed rows). */
  onRowClick?: () => void;
  /** While true, the checkbox shows as checked (filled) and inert, and the row does not open its
   * edit surface — used during the optimistic complete + undo window, before the mutation
   * commits. */
  isCompleting?: boolean;
  /** While true, a completed item's revert is pending/committing — the checkbox shows unchecked
   * and inert, the mirror image of `isCompleting`, before the mutation commits. */
  isReverting?: boolean;
  /** Accessible label for the complete checkbox (pre-translated by the page). */
  completeLabel?: string;
  /** Accessible label for a completed item's revert checkbox (pre-translated by the page). */
  uncompleteLabel?: string;
  /** Read-only row: no checkbox, not clickable — for a todo assigned to another user (shown for
   * context but not editable or completable by the viewer). */
  readOnly?: boolean;
}

function KindIcon({ kind }: { kind: TodoKind | undefined }) {
  if (kind === "workflow") return <Repeat />;
  if (kind === "recap") return <Note />;
  if (kind === "todo") return <TodosIcon />;
  return null;
}

export function TodoListItem({
  title,
  isCompleted = false,
  kind,
  kindLabel,
  dueDateLabel,
  overdueLabel,
  assigneeName,
  customerName,
  onCustomerClick,
  onComplete,
  onUncomplete,
  onRowClick,
  isCompleting = false,
  isReverting = false,
  completeLabel,
  uncompleteLabel,
  readOnly = false,
}: TodoListItemProps) {
  const state = isCompleted ? "completed" : "active";
  // The api rejects description and due-date edits once a todo is COMPLETED.
  const rowInteraction =
    onRowClick && !isCompleting
      ? {
          "data-clickable": "true" as const,
          role: "button",
          tabIndex: 0,
          onClick: onRowClick,
          onKeyDown: (event: KeyboardEvent) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onRowClick();
            }
          },
        }
      : {};
  const assigneePiece = assigneeName ? (
    <MetaPiece key="assignee">
      <UserOutline aria-hidden="true" />
      {assigneeName}
    </MetaPiece>
  ) : null;

  const customerPrefix = customerName ? (
    <CustomerLink
      role="link"
      tabIndex={0}
      data-state={state}
      onClick={(e) => {
        e.stopPropagation();
        onCustomerClick?.();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          e.stopPropagation();
          onCustomerClick?.();
        }
      }}
    >
      @{customerName}{" "}
    </CustomerLink>
  ) : null;

  const kindPiece = kindLabel ? (
    <MetaPiece key="kind">
      <KindIcon kind={kind} />
      {kindLabel}
    </MetaPiece>
  ) : null;

  if (isCompleted) {
    // A completed item only shows its title. With neither title nor description there's nothing to
    // render, so return null instead of an empty <Row> — an empty row would still be a DOM child and
    // become the `:last-child`, pushing the divider border onto the last *visible* item above it.
    if (!title) {
      return null;
    }
    const completedMeta: ReactNode[] = [];
    if (assigneePiece) {
      completedMeta.push(assigneePiece);
    }
    // The read-only bucket mixes recap and manual todos, so it keeps its kind once completed.
    if (readOnly && kindPiece) {
      if (completedMeta.length > 0) completedMeta.push(<MetaSeparator key="sep-kind">·</MetaSeparator>);
      completedMeta.push(kindPiece);
    }
    if (dueDateLabel) {
      if (completedMeta.length > 0) completedMeta.push(<MetaSeparator key="sep-due">·</MetaSeparator>);
      completedMeta.push(
        <MetaPiece key="due">
          <Calendar />
          {dueDateLabel}
        </MetaPiece>,
      );
    }

    return (
      <Row data-state={state}>
        {!readOnly && (
          <Checkbox
            type="button"
            aria-label={uncompleteLabel}
            data-checked={isReverting ? undefined : "completed"}
            disabled={isReverting || !onUncomplete}
            onClick={(event) => {
              event.stopPropagation();
              onUncomplete?.();
            }}
          >
            {!isReverting && <Checkmark aria-hidden="true" />}
          </Checkbox>
        )}
        <Body>
          <Title data-state={state}>
            {customerPrefix}
            {title}
          </Title>
          {(assigneePiece || (readOnly && kindPiece)) && <Meta>{completedMeta}</Meta>}
        </Body>
      </Row>
    );
  }

  const metaParts: ReactNode[] = [];
  if (assigneePiece) {
    metaParts.push(assigneePiece);
  }
  if (kindPiece) {
    if (metaParts.length > 0) metaParts.push(<MetaSeparator key="sep-kind">·</MetaSeparator>);
    metaParts.push(kindPiece);
  }
  if (overdueLabel) {
    if (metaParts.length > 0) metaParts.push(<MetaSeparator key="sep-overdue">·</MetaSeparator>);
    metaParts.push(
      <Overdue key="overdue">
        <Calendar />
        {overdueLabel}
      </Overdue>,
    );
  } else if (dueDateLabel) {
    if (metaParts.length > 0) metaParts.push(<MetaSeparator key="sep-due">·</MetaSeparator>);
    metaParts.push(
      <MetaPiece key="due">
        <Calendar />
        {dueDateLabel}
      </MetaPiece>,
    );
  }

  return (
    <Row data-state={state} {...(readOnly ? {} : rowInteraction)}>
      {!readOnly && (
        <Checkbox
          type="button"
          aria-label={completeLabel}
          data-checked={isCompleting}
          disabled={isCompleting}
          onClick={(event) => {
            event.stopPropagation();
            onComplete?.();
          }}
        >
          {isCompleting && <Checkmark aria-hidden="true" />}
        </Checkbox>
      )}
      <Body>
        {(title || customerPrefix) && (
          <Title data-state={state}>
            {customerPrefix}
            {title}
          </Title>
        )}
        {metaParts.length > 0 && <Meta>{metaParts}</Meta>}
      </Body>
    </Row>
  );
}
